/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GrantWorkflowPermissionsV2PathParameters,
  LeaveWorkflowV2PathParameters,
  ListWorkflowPermissionsV2Params,
  ListWorkflowPermissionsV2PathParameters,
  RevokeWorkflowPermissionsV2PathParameters,
  TLGrantPermissionsRequest,
  TLHTTPValidationError,
  TLLeaveWorkflowResponse,
  TLPermissionListResponse,
  TLWorkflowPermission,
  TLWorkflowsV2RevokePermissionsRequest,
  TLWorkflowsV2RevokePermissionsResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Workflow Permissions V2
 */
export const listWorkflowPermissionsV2 = (
  { workflowId }: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPermissionListResponse>(
    { url: `/api/v2/workflows/${workflowId}/permissions`, method: 'GET', params, signal },
    options,
  );
};

export const getListWorkflowPermissionsV2QueryKey = (
  { workflowId }: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
) => {
  return ['api', 'v2', 'workflows', workflowId, 'permissions', ...(params ? [params] : [])] as const;
};

export const getListWorkflowPermissionsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWorkflowPermissionsV2QueryKey({ workflowId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>> = ({ signal }) =>
    listWorkflowPermissionsV2({ workflowId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListWorkflowPermissionsV2QueryResult = NonNullable<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>>;
export type ListWorkflowPermissionsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useListWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListWorkflowPermissionsV2PathParameters,
  params: undefined | ListWorkflowPermissionsV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
          TError,
          Awaited<ReturnType<typeof listWorkflowPermissionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
          TError,
          Awaited<ReturnType<typeof listWorkflowPermissionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Workflow Permissions V2
 */

export function useListWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWorkflowPermissionsV2QueryOptions({ workflowId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Workflow Permissions V2
 */
export const prefetchListWorkflowPermissionsV2Query = async <
  TData = Awaited<ReturnType<typeof listWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListWorkflowPermissionsV2QueryOptions({ workflowId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Workflow Permissions V2
 */
export const invalidateListWorkflowPermissionsV2 = async (
  queryClient: QueryClient,
  { workflowId }: ListWorkflowPermissionsV2PathParameters,
  params?: ListWorkflowPermissionsV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListWorkflowPermissionsV2QueryKey({ workflowId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Grant Workflow Permissions V2
 */
export const grantWorkflowPermissionsV2 = (
  { workflowId }: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowPermission[]>(
    {
      url: `/api/v2/workflows/${workflowId}/permissions`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLGrantPermissionsRequest,
      signal,
    },
    options,
  );
};

export const getGrantWorkflowPermissionsV2QueryKey = (
  { workflowId }: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest?: BodyType<TLGrantPermissionsRequest>,
) => {
  return ['POST', 'api', 'v2', 'workflows', workflowId, 'permissions', tLGrantPermissionsRequest] as const;
};

export const getGrantWorkflowPermissionsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGrantWorkflowPermissionsV2QueryKey({ workflowId }, tLGrantPermissionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>> = ({ signal }) =>
    grantWorkflowPermissionsV2({ workflowId }, tLGrantPermissionsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GrantWorkflowPermissionsV2QueryResult = NonNullable<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>>;
export type GrantWorkflowPermissionsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useGrantWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
          TError,
          Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGrantWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
          TError,
          Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGrantWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Grant Workflow Permissions V2
 */

export function useGrantWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGrantWorkflowPermissionsV2QueryOptions({ workflowId }, tLGrantPermissionsRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Grant Workflow Permissions V2
 */
export const prefetchGrantWorkflowPermissionsV2Query = async <
  TData = Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof grantWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGrantWorkflowPermissionsV2QueryOptions({ workflowId }, tLGrantPermissionsRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Grant Workflow Permissions V2
 */
export const invalidateGrantWorkflowPermissionsV2 = async (
  queryClient: QueryClient,
  { workflowId }: GrantWorkflowPermissionsV2PathParameters,
  tLGrantPermissionsRequest: BodyType<TLGrantPermissionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getGrantWorkflowPermissionsV2QueryKey({ workflowId }, tLGrantPermissionsRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Leave Workflow V2
 */
export const leaveWorkflowV2 = (
  { workflowId }: LeaveWorkflowV2PathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLLeaveWorkflowResponse>(
    { url: `/api/v2/workflows/${workflowId}/permissions/me`, method: 'DELETE', signal },
    options,
  );
};

export const getLeaveWorkflowV2QueryKey = ({ workflowId }: LeaveWorkflowV2PathParameters) => {
  return ['DELETE', 'api', 'v2', 'workflows', workflowId, 'permissions', 'me'] as const;
};

export const getLeaveWorkflowV2QueryOptions = <
  TData = Awaited<ReturnType<typeof leaveWorkflowV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: LeaveWorkflowV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getLeaveWorkflowV2QueryKey({ workflowId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof leaveWorkflowV2>>> = ({ signal }) =>
    leaveWorkflowV2({ workflowId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type LeaveWorkflowV2QueryResult = NonNullable<Awaited<ReturnType<typeof leaveWorkflowV2>>>;
export type LeaveWorkflowV2QueryError = ErrorType<TLHTTPValidationError>;

export function useLeaveWorkflowV2<
  TData = Awaited<ReturnType<typeof leaveWorkflowV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: LeaveWorkflowV2PathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof leaveWorkflowV2>>,
          TError,
          Awaited<ReturnType<typeof leaveWorkflowV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLeaveWorkflowV2<
  TData = Awaited<ReturnType<typeof leaveWorkflowV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: LeaveWorkflowV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof leaveWorkflowV2>>,
          TError,
          Awaited<ReturnType<typeof leaveWorkflowV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useLeaveWorkflowV2<
  TData = Awaited<ReturnType<typeof leaveWorkflowV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: LeaveWorkflowV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Leave Workflow V2
 */

export function useLeaveWorkflowV2<
  TData = Awaited<ReturnType<typeof leaveWorkflowV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: LeaveWorkflowV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getLeaveWorkflowV2QueryOptions({ workflowId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Leave Workflow V2
 */
export const prefetchLeaveWorkflowV2Query = async <
  TData = Awaited<ReturnType<typeof leaveWorkflowV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: LeaveWorkflowV2PathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof leaveWorkflowV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getLeaveWorkflowV2QueryOptions({ workflowId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Leave Workflow V2
 */
export const invalidateLeaveWorkflowV2 = async (
  queryClient: QueryClient,
  { workflowId }: LeaveWorkflowV2PathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getLeaveWorkflowV2QueryKey({ workflowId }) }, options);

  return queryClient;
};

/**
 * @summary Revoke Workflow Permissions V2
 */
export const revokeWorkflowPermissionsV2 = (
  { workflowId }: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLWorkflowsV2RevokePermissionsResponse>(
    {
      url: `/api/v2/workflows/${workflowId}/permissions/revoke`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLWorkflowsV2RevokePermissionsRequest,
      signal,
    },
    options,
  );
};

export const getRevokeWorkflowPermissionsV2QueryKey = (
  { workflowId }: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest?: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
) => {
  return [
    'POST',
    'api',
    'v2',
    'workflows',
    workflowId,
    'permissions',
    'revoke',
    tLWorkflowsV2RevokePermissionsRequest,
  ] as const;
};

export const getRevokeWorkflowPermissionsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getRevokeWorkflowPermissionsV2QueryKey({ workflowId }, tLWorkflowsV2RevokePermissionsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>> = ({ signal }) =>
    revokeWorkflowPermissionsV2({ workflowId }, tLWorkflowsV2RevokePermissionsRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: workflowId !== null && workflowId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RevokeWorkflowPermissionsV2QueryResult = NonNullable<
  Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>
>;
export type RevokeWorkflowPermissionsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useRevokeWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
          TError,
          Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
          TError,
          Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRevokeWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Revoke Workflow Permissions V2
 */

export function useRevokeWorkflowPermissionsV2<
  TData = Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { workflowId }: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRevokeWorkflowPermissionsV2QueryOptions(
    { workflowId },
    tLWorkflowsV2RevokePermissionsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Revoke Workflow Permissions V2
 */
export const prefetchRevokeWorkflowPermissionsV2Query = async <
  TData = Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { workflowId }: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof revokeWorkflowPermissionsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRevokeWorkflowPermissionsV2QueryOptions(
    { workflowId },
    tLWorkflowsV2RevokePermissionsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Revoke Workflow Permissions V2
 */
export const invalidateRevokeWorkflowPermissionsV2 = async (
  queryClient: QueryClient,
  { workflowId }: RevokeWorkflowPermissionsV2PathParameters,
  tLWorkflowsV2RevokePermissionsRequest: BodyType<TLWorkflowsV2RevokePermissionsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRevokeWorkflowPermissionsV2QueryKey({ workflowId }, tLWorkflowsV2RevokePermissionsRequest) },
    options,
  );

  return queryClient;
};
